import _ from 'lodash';
import { camelize } from 'helpers/syntaxHelper';
import { getOrderedColumns } from 'helpers/chartHelper';

import { getYearlyComparisonScale } from '../yearlyComparison/scale';

import { pivotYearlyComparison } from './pivot';
import { cumulateMetrics } from './cumulate';
import { FormatVizDataArgs } from './formatVizData';
import { canCumulateDataTypes } from 'business/definitions';
import { canCumulateVizTypes } from 'helpers/vizInfos';
import { EffectiveRequestFilter } from 'components/ChartContainer/types';
import { extractCompletionDateFromTimeFilter } from './timeFilterHelper';

type FormatTableDataArgs = FormatVizDataArgs & {
    queryDimensions: QueryDimensionT[];
    timeFilters: EffectiveRequestFilter[] | QueryFilterT[];
};

export function formatTableData({
    data,
    columns,
    settings,
    queryMetrics,
    vizType,
    queryDimensions,
    timeFilters,
}: FormatTableDataArgs): FlowAnyObject {
    // TODO: improve typing
    // We start with operations that can modify the columns

    // There are three pivots. Multimetrics, yearly comparison and regular pivot on a specific column
    // We start by multimetrics, which happens only for charts, then yearly comparison and finally pivot on
    // a column, which happens only for tables
    const metricsKeys = queryMetrics.map((m) => camelize(m.field));
    // TODO: Check if isYearlyComparison should be forced to true when pivotField refers a date column with month scale
    const { isYearlyComparison, reverseGroupBy } = settings;

    const completionDate = extractCompletionDateFromTimeFilter(
        data,
        queryDimensions,
        timeFilters,
    );

    // Filtering to avoid cumulating non-existent metrics
    let { metricsCumulated } = settings;
    metricsCumulated = _.filter(metricsCumulated, (m) =>
        metricsKeys.includes(m),
    );

    let formattedDict = {
        chartData: _.cloneDeep(data),
        tableColumns: _.cloneDeep(columns).map((col) => ({
            ...col,
            isMetric: metricsKeys.includes(col.key),
        })),
    };

    if (isYearlyComparison) {
        formattedDict = pivotYearlyComparison(
            formattedDict.chartData,
            formattedDict.tableColumns,
        );
    }

    // TODO: Synchronize the getOrderedColumns with the one in formatVizData. If the x value here is not
    // the same one than the value in formatVizData the cumulate will not work.
    if (metricsCumulated && metricsCumulated.length) {
        let sumColumnKey;
        if (isYearlyComparison) {
            sumColumnKey = getYearlyComparisonScale(columns);
        } else {
            // We simply grab the x column to know on which dimension we should sum
            const orderedColumns = getOrderedColumns(
                formattedDict.chartData,
                formattedDict.tableColumns,
                vizType,
                reverseGroupBy,
                isYearlyComparison,
            );
            // Sometimes a metric can be cumulated, but then the user changes the dimension,
            // which makes the cumulate settings disappear, since the dimension doesn't have the right
            // type. Here we ensure that even if that happens, we will not cumulate the data.
            // This should not happen since we reset settings when dimensions are changed,
            // but we leave it here in case some old charts have an issue
            if (
                orderedColumns.x &&
                canCumulateDataTypes.includes(orderedColumns.x.type) &&
                canCumulateVizTypes.includes(vizType)
            ) {
                sumColumnKey = orderedColumns.x.key;
            }
        }

        if (sumColumnKey) {
            formattedDict.chartData = cumulateMetrics(
                formattedDict.chartData,
                formattedDict.tableColumns,
                metricsCumulated,
                sumColumnKey,
                completionDate,
            );
        } else {
            console.error('Could not find a column on which to sum');
        }
    }

    return formattedDict;
}
